
export interface ParsedRole {
  name: string;
  application: string | null;
  tenantId: string | null;
}

export interface UserRoleMetadata {
  userApplications: Set<string>;
  userTenantIds: Set<string>;
  isDCSAC: boolean;
  isUMAdmin: boolean;
}

export class RoleUtils {
  private static parseRoleCache = new Map<string, ParsedRole>();

  static parseRole(role: string): ParsedRole {
    if (!role) {
      return { name: '', application: null, tenantId: null };
    }

    const cached = this.parseRoleCache.get(role);
    if (cached) {
      return cached;
    }

    let parsed: ParsedRole;

    if (role.includes('~')) {
      const [name, context] = role.split('~');

      if (!context) {
        parsed = { name, application: null, tenantId: null };
      } else {
        const [application, tenantId] = context.split('_');
        parsed = {
          name,
          application: application || null,
          tenantId: tenantId || null,
        };
      }
    } else {
      parsed = {
        name: role,
        application: null,
        tenantId: null,
      };
    }

    this.parseRoleCache.set(role, parsed);
    return parsed;
  }

  static buildRole(
    name: string,
    application?: string | null,
    tenantId?: string | null
  ): string {
    if (!application && !tenantId) {
      return name;
    }
    return `${name}~${application || ''}_${tenantId || ''}`;
  }

  static extractApplicationFromRole(role: string): string | null {
    return RoleUtils.parseRole(role).application;
  }

  static extractTenantIdFromRole(role: string): string | null {
    return RoleUtils.parseRole(role).tenantId;
  }

  static getRoleNameWithoutContext(role: string): string {
    return RoleUtils.parseRole(role).name;
  }

  static isSACRole(role: string): boolean {
    const parsed = RoleUtils.parseRole(role);
    return parsed.name === 'UM_SAC' || parsed.name === 'UM_DC_SAC';
  }

  static isDCSACRole(role: string): boolean {
    const parsed = RoleUtils.parseRole(role);
    return parsed.name === 'UM_DC_SAC';
  }

  static hasConflictingSACRoles(existingRoles: string[], newRole: string): boolean {
    if (!existingRoles || existingRoles.length === 0) {
      return false;
    }
    
    const newApp = RoleUtils.extractApplicationFromRole(newRole);
    
    if (!RoleUtils.isSACRole(newRole) || !newApp) {
      return false;
    }
    
    const isNewDCSAC = RoleUtils.isDCSACRole(newRole);
    
    for (const existingRole of existingRoles) {
      if (!RoleUtils.isSACRole(existingRole)) {
        continue;
      }
      
      const existingApp = RoleUtils.extractApplicationFromRole(existingRole);
      if (existingApp !== newApp) {
        continue;
      }
      
      const isExistingDCSAC = RoleUtils.isDCSACRole(existingRole);
      
      if (isNewDCSAC !== isExistingDCSAC) {
        return true;
      }
    }
    
    return false;
  }

  static isRoleTenantSpecific(roleKey: string): boolean {
    const parsed = RoleUtils.parseRole(roleKey);
    return parsed.tenantId !== null && parsed.tenantId !== '';
  }

  static getRoleTenantId(roleKey: string): string | null {
    return RoleUtils.parseRole(roleKey).tenantId;
  }

  static isApplicationScopedRole(role: string): boolean {
    if (!role) return false;

    const parsed = RoleUtils.parseRole(role);

    return parsed.application !== null && parsed.application !== '';
  }

  static normalizeApplicationName(appName: string): string {
    if (!appName) return '';
    return appName.replace(/[\s_]/g, '-').toLowerCase();
  }

  static formatApplicationNameForDisplay(appName: string): string {
    if (!appName) return '';
    
    const knownEnvironments = [
      'dev',
      'qa',
      'uat',
      'prod',
      'perf',
      'staging',
    ];
    
    const uppercaseWords = [
      'ui',
      'db',
      'id',
      'us',
      'eu',
      'uk',
      'api',
      'rh',
    ];
    
    const withSpaces = appName.replace(/-/g, ' ');
    return withSpaces
      .split(' ')
      .map((word) => {
        const lowerWord = word.toLowerCase();
        
        if (uppercaseWords.includes(lowerWord)) {
          return word.toUpperCase();
        }
        
        const envMatch = knownEnvironments.find((env) =>
          lowerWord.startsWith(env) && /^\d+$/.test(lowerWord.slice(env.length))
        );
        if (envMatch) {
          return word.toUpperCase();
        }
        
        if (knownEnvironments.includes(lowerWord)) {
          return word.toUpperCase();
        }
        
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  }

  static getDisplayInfo(role: string) {
    const parsed = RoleUtils.parseRole(role);
    const formattedApp = parsed.application
      ? RoleUtils.formatApplicationNameForDisplay(parsed.application)
      : '';

    return {
      name: parsed.name,
      application: parsed.application,
      tenantId: parsed.tenantId,
      displayName: parsed.name,
      contextLabel:
        parsed.application || parsed.tenantId
          ? `${formattedApp}${
              parsed.tenantId ? ' (' + parsed.tenantId + ')' : ''
            }`
          : null,
    };
  }

  static hasAccessToApplication(
    userRoles: string[],
    applicationId: string
  ): boolean {
    if (!userRoles || !applicationId) {
      return false;
    }

    return userRoles.some((role) => {
      const parsed = RoleUtils.parseRole(role);
      
      if (parsed.name && parsed.name.startsWith('UM_')) {
        return false;
      }
      
      return parsed.application === applicationId;
    });
  }

  static getAccessibleApplications(userRoles: string[]): Set<string> {
    const applications = new Set<string>();

    if (!userRoles) {
      return applications;
    }

    userRoles.forEach((role) => {
      const parsed = RoleUtils.parseRole(role);
      
      if (parsed.name && parsed.name.startsWith('UM_')) {
        return;
      }
      
      if (parsed.application && parsed.application !== '') {
        applications.add(parsed.application);
      }
    });

    return applications;
  }

  static normalizeTenantId(tenantId: string | null): string {
    if (!tenantId) {
      return tenantId || '';
    }
    return tenantId.replace(/^0+/, '') || '0';
  }

  static  computeUserRoleMetadata(userRoles: string[]): UserRoleMetadata {
    const userApplications = new Set<string>();
    const userTenantIds = new Set<string>();
    let isDCSAC = false;
    let isUMAdmin = false;

    userRoles.forEach((r) => {
      if (r === 'UM_ADMINISTRATOR') {
        isUMAdmin = true;
        return;
      }

      if (RoleUtils.isSACRole(r)) {
        const app = RoleUtils.extractApplicationFromRole(r);
        const tenantId = RoleUtils.extractTenantIdFromRole(r);
        if (app) userApplications.add(app);
        if (tenantId) {
          userTenantIds.add(RoleUtils.normalizeTenantId(tenantId));
        }
        if (RoleUtils.isDCSACRole(r)) {
          isDCSAC = true;
        }
      }
    });

    return { userApplications, userTenantIds, isDCSAC, isUMAdmin };
  }

  static canUserAccessRoleOptimized(
    roleKey: string,
    roleApp: string | null,
    metadata: UserRoleMetadata
  ): boolean {
    if (metadata.isUMAdmin) {
      return true;
    }

    const roleTenantId = RoleUtils.extractTenantIdFromRole(roleKey);
    const isRoleSAC = RoleUtils.isSACRole(roleKey);

    if (roleApp && !metadata.userApplications.has(roleApp)) {
      return false;
    }

    if (metadata.isDCSAC) {
      if (roleTenantId && roleTenantId !== '') {
        const normalizedRoleTenantId = RoleUtils.normalizeTenantId(roleTenantId);
        return metadata.userTenantIds.has(normalizedRoleTenantId);
      }
      return false;
    }

    if (isRoleSAC) {
      return true;
    }

    return !roleTenantId || roleTenantId === '';
  }

  static canUserAccessRole(
    userRoles: string[],
    roleKey: string,
    roleApp: string | null,
    isUMAdmin: boolean
  ): boolean {
    if (isUMAdmin) {
      return true;
    }

    const roleTenantId = RoleUtils.extractTenantIdFromRole(roleKey);
    const isRoleSAC = RoleUtils.isSACRole(roleKey);

    const userApplications = new Set<string>();
    const userTenantIds = new Set<string>();
    let isDCSAC = false;

    userRoles.forEach((r) => {
      if (RoleUtils.isSACRole(r)) {
        const app = RoleUtils.extractApplicationFromRole(r);
        const tenantId = RoleUtils.extractTenantIdFromRole(r);
        if (app) userApplications.add(app);
        if (tenantId) {
          userTenantIds.add(RoleUtils.normalizeTenantId(tenantId));
        }
        if (RoleUtils.isDCSACRole(r)) {
          isDCSAC = true;
        }
      }
    });

    if (roleApp && !userApplications.has(roleApp)) {
      return false;
    }

    if (isDCSAC) {
      if (roleTenantId && roleTenantId !== '') {
        const normalizedRoleTenantId = RoleUtils.normalizeTenantId(roleTenantId);
        return userTenantIds.has(normalizedRoleTenantId);
      }
      return false;
    }

    if (isRoleSAC) {
      return true;
    }

    return !roleTenantId || roleTenantId === '';
  }
}
